import React, { useEffect } from 'react';
import {
  composeMatchStatus,
  MatchContext,
  statusTypes,
  useLocalMatchDateTime,
  useMediaProviders,
} from '../../utils';
import { useReactAppState } from '../../../shared/containers/utils';
import {
  actionTypes as matchInfoActionTypes,
  useMatchInfo,
} from '../../../shared/hooks/useMatchInfo';
import {
  actionTypes as matchStatusActionTypes,
  useMatchStatus,
} from '../../../shared/hooks/useMatchStatus';
import { composeClubMatch } from './utils';

function MatchDataLayer({
  matchStatus,
  children,
  forgeMatch,
  listType,
  displayMatchDate,
  extraCssClasses,
  matchSportecId,
  matchPageUrl,
  useEmptyClasses = false,
  useMatchData = false,
  queryClassName = null,
  delayedMatch = false,
  statsAPIMatch = {},
}) {
  const {
    broadcasters,
    homeClubBroadcasters,
    awayClubBroadcasters,
    clubBroadcasters,
    leagueMatchTitle,
    mgmId,
    firstPartyTickets,
    thirdPartyTickets,
    hotelBooking,
    sponsorImage,
    promotionalStay,
    promotionalSponsor,
    promotionalRide,
    promotionalMisc1,
    promotionalMisc2,
    promotionalFly,
    promo,
    slug,
    previous_match_id,
    leg,
    result_type,
    appleSubscriptionTier,
    appleStreamURL,
    roundName,
    roundGroup,
    competitionPhase,
    season: forgeSeason,
    competition: forgeCompetition,
    slug: forgeSlug,
  } = forgeMatch || {};

  const { name: forgeSeasonYear } = forgeSeason || {};
  const { slug: forgeCompetitionSlug } = forgeCompetition || {};

  const {
    recentGoal,
    matchStatusOverride,
    urlList,
    directoryList,
  } = useReactAppState();

  const { status, updateMatchStatus, queryConfig, mlsMatch } = useMatchStatus({
    matchData: useMatchData ? statsAPIMatch : null,
    initialState: {
      matchSportecId,
      status: composeMatchStatus({
        period: statsAPIMatch?.match_information?.match_status,
        delayedMatch: delayedMatch|| forgeMatch?.delayedMatch,
      }),
      matchStatusOverride,
    },
  });

  const {
    away = {},
    home = {},
    match_information = {},
    environment = {},
  } = statsAPIMatch;

  const {
    planned_kickoff_time: matchDate,
    kickoff_time: matchKickOffTime,
    match_status: period,
    match_type,
    minute_of_play: minute_display,
    away_team_seed: awayClubRank,
    home_team_seed: homeClubRank,
    match_date_time_status,
  } = match_information;

  const stadium_name =
    match_information?.stadium_name || environment?.stadium_name;

  const isTimeTbd =
    match_date_time_status?.toLowerCase() === "time tbd" ?? false;

  const is_final = match_information.match_status?.includes("final");

  const competition = {
    slug: match_information.competition_slug || forgeMatch?.competition?.slug,
    sportecId: match_information.competition_id,
    shortName: match_information.competition_name,
    name: match_information.competition_name,
    type: match_information.competition_type,
  };

  const venue = {
    name: stadium_name,
  };

  const season = {
    sportecId: match_information.season_id,
    season: match_information.season,
  };

  const homeClubMatch = composeClubMatch({
    club: {
      ...home,
      side: 'home',
      goals: match_information.home_team_goals,
      penalty_goals: match_information.home_team_penalty_goals,
    },
    match_information,
  });

  const awayClubMatch = composeClubMatch({
    club: {
      ...away,
      side: 'away',
      goals: match_information.away_team_goals,
      penalty_goals: match_information.away_team_penalty_goals,
    },
    match_information,
  });

  const { baseUrl } = urlList || {};
  const { matchHubDir } = directoryList || {};
  const fallbackMatchPageUrl = matchPageUrl
    ? matchPageUrl
    : forgeCompetitionSlug && forgeSlug
    ? `${baseUrl}/${matchHubDir}/${forgeCompetitionSlug}/${forgeSeasonYear}/matches/${forgeSlug}`
    : null;

  const {
    score: homeScore,
    shootout_score: homeShootoutScore,
    extra_score: homeExtraScore,
    is_winner: isHomeWinner,
    is_loser: isHomeLoser,
    formation: homeInitialFormation,
  } = homeClubMatch || {};

  const {
    score: awayScore,
    shootout_score: awayShootoutScore,
    extra_score: awayExtraScore,
    is_winner: isAwayWinner,
    is_loser: isAwayLoser,
    formation: awayInitialFormation,
  } = awayClubMatch || {};

  const { setIsRecentGoal } = recentGoal || {};

  const { state, updateMatchInfo } = useMatchInfo({
    initialState: {
      period,
      isRecentGoal: false,
      count: 0,
      isGoalTimerRunning: false,
      home,
      away,
      homeScore,
      awayScore,
      isFinal: is_final,
      homeShootoutScore,
      awayShootoutScore,
      homeExtraScore,
      awayExtraScore,
      winnerStatus: {
        isHomeWinner,
        isHomeLoser,
        isAwayWinner,
        isAwayLoser,
      },
      goals: [],
      penaltyKicks: [],
      minuteDisplay: minute_display,
      matchType: match_type,
      resultType: result_type,
      homePlayers: [],
      awayPlayers: [],
    },
  });

  const {
    resultType,
    isFinal,
    matchType,
    period: matchPeriod,
    homeScore: updatedHomeScore,
    homeShootoutScore: updatedHomeShootoutScore,
    awayScore: updatedAwayScore,
    awayShootoutScore: updatedAwayShootoutScore,
    penaltyKicks,
    goals,
    minuteDisplay,
    isRecentGoal,
    count,
    winnerStatus,
  } = state || {};

  const periodAbbreviation = status?.abbreviation?.toLowerCase();

  const composedMatchDateTime = useLocalMatchDateTime(matchDate);
  const composedMatchKickOffTime = useLocalMatchDateTime(matchKickOffTime);
  const mediaProviders = useMediaProviders({
    broadcasters,
    homeClubBroadcasters,
    awayClubBroadcasters,
    clubBroadcasters,
  });

  const homeClubData = {
    ...home,
    ...homeClubMatch,
    sportecId: homeClubMatch.id,
    homeScore: homeClubMatch.score,
    homeInitialFormation,
    homeShootoutScore: homeClubMatch.shootout_score,
    logoColorSlug: forgeMatch?.home?.logoColorSlug,
    logoColorUrl: forgeMatch?.home?.logoColorUrl,
    crestColorSlug: forgeMatch?.home?.crestColorSlug,
    slug: forgeMatch?.home?.slug,
  };
  const awayClubData = {
    ...away,
    ...awayClubMatch,
    sportecId: awayClubMatch.id,
    awayScore: awayClubMatch.score,
    awayInitialFormation,
    awayShootoutScore: awayClubMatch.shootout_score,
    logoColorSlug: forgeMatch?.away?.logoColorSlug,
    logoColorUrl: forgeMatch?.away?.logoColorUrl,
    crestColorSlug: forgeMatch?.away?.crestColorSlug,
    slug: forgeMatch?.away?.slug,
  };

  const hidePromoClass =
    promo ||
    promotionalFly ||
    promotionalSponsor ||
    promotionalMisc1 ||
    promotionalMisc2 ||
    promotionalRide ||
    promotionalStay
      ? ''
      : 'mls-o-match-strip--hide-promo';

  useEffect(() => {
    const {
      home,
      away,
      result_type: resultTypeFromLiveData,
      match_information,
    } = mlsMatch || {};

    const { match_status, home_team_goals, away_team_goals, minute_of_play } =
      match_information || mlsMatch || {};

    const {
      extra_score: homeExtraScore,
      shootout_score: homeShootoutScore,
      is_winner: isHomeWinner,
      is_series_winner: isHomeSeriesWinner,
      is_loser: isHomeLoser,
      is_series_loser: isHomeSeriesLoser,
    } = home || {};
    const {
      extra_score: awayExtraScore,
      shootout_score: awayShootoutScore,
      is_winner: isAwayWinner,
      is_series_winner: isAwaySeriesWinner,
      is_loser: isAwayLoser,
      is_series_loser: isAwaySeriesLoser,
    } = away || {};

    const liveWinnerStatus = {
      isHomeWinner: isHomeWinner || isHomeSeriesWinner,
      isHomeLoser: isHomeLoser || isHomeSeriesLoser,
      isAwayWinner: isAwayWinner || isAwaySeriesWinner,
      isAwayLoser: isAwayLoser || isAwaySeriesLoser,
    };

    const matchStatus = composeMatchStatus({
      period: match_status,
      isFinal,
      delayedMatch:delayedMatch|| forgeMatch?.delayedMatch,
    });

    if (matchStatus?.abbreviation === statusTypes.live || matchStatus?.abbreviation === statusTypes.delayed) {
      updateMatchInfo({
        type: matchInfoActionTypes.goals,
        payload: { home_team_goals, away_team_goals, minute_of_play },
      });
      updateMatchStatus({
        type: matchStatusActionTypes.status,
        payload: matchStatus,
      });
      updateMatchInfo({
        type: matchInfoActionTypes.homeExtraScore,
        payload: homeExtraScore,
      });
      updateMatchInfo({
        type: matchInfoActionTypes.awayExtraScore,
        payload: awayExtraScore,
      });
      updateMatchInfo({
        type: matchInfoActionTypes.winnerStatus,
        payload: liveWinnerStatus,
      });
      updateMatchInfo({
        type: matchInfoActionTypes.resultType,
        payload: resultTypeFromLiveData,
      });
      updateMatchInfo({
        type: matchInfoActionTypes.minuteDisplay,
        payload: minute_of_play,
      });
      updateMatchInfo({
        type: matchInfoActionTypes.homeScore,
        payload: home_team_goals,
      });
      updateMatchInfo({
        type: matchInfoActionTypes.awayScore,
        payload: away_team_goals,
      });
      updateMatchInfo({
        type: matchInfoActionTypes.homeShootoutScore,
        payload: homeShootoutScore,
      });
      updateMatchInfo({
        type: matchInfoActionTypes.awayShootoutScore,
        payload: awayShootoutScore,
      });
    }
  }, [mlsMatch, isFinal]);

  useEffect(() => {
    setIsRecentGoal(isRecentGoal);
  }, [isRecentGoal]);

  const classes = useEmptyClasses
    ? ''
    : `mls-o-match-strip mls-o-match-strip--${periodAbbreviation} mls-o-match-strip--${listType} ${hidePromoClass} ${
        extraCssClasses ? extraCssClasses : ''
      }`;

  const Wrapper = useEmptyClasses ? React.Fragment : 'div';

  const props = classes ? { className: classes } : {};

  return (
    <Wrapper {...props}>
      <MatchContext.Provider
        value={{
          matchDateTime: composedMatchDateTime,
          matchKickOffTime: composedMatchKickOffTime,
          leg,
          slug,
          competition,
          season,
          leagueMatchTitle,
          mediaProviders,
          mgmId,
          venue,
          winnerStatus,
          homeClubData,
          awayClubData,
          isFinal,
          matchType,
          status,
          listType,
          resultType,
          promoInfo: {
            hotelBooking,
            matchPromo: promo,
            promotionalMisc1,
            promotionalMisc2,
            promotionalFly,
            promotionalRide,
            promotionalStay,
            promotionalSponsor,
            firstPartyTickets,
            thirdPartyTickets,
            sponsorImage,
          },
          minuteDisplay,
          displayMatchDate: !!(
            displayMatchDate && composedMatchDateTime != null
          ),
          isRecentGoal,
          count,
          goals,
          period: matchPeriod,
          penaltyKicks,
          isTimeTbd,
          matchSportecId,
          queryConfig,
          previousMatchId: previous_match_id,
          matchPageUrl: fallbackMatchPageUrl,
          appleSubscriptionTier,
          appleStreamURL,
          roundName,
          roundGroup,
          competitionPhase,
          homeClubRank,
          awayClubRank,
          mlsMatch: mlsMatch || statsAPIMatch,
        }}
      >
        {children}
      </MatchContext.Provider>
    </Wrapper>
  );
}

export default MatchDataLayer;
